/**
 * Założenia pieniędzy w teczce, wypisane zdaniami dla klienta (zasada „nie obiecujemy pieniędzy bez
 * podstaw”: widełki, scenariusz bez wzrostu cen i jawne założenia z datą). Te same zdania pokazuje ekran
 * Projektu 3D, teczka i jej PDF, więc żyją tutaj, a nie w komponencie.
 */

import { taryfaPoKluczu, STAN_TARYF, ZRODLO_CEN_TARYF } from '../../config/taryfy'
import { WSPOLCZYNNIK_DEPOZYTU, ZMIENNOSC_PRODUKCJI } from '../../config/cenyEnergii'
import { DEGRADACJA_ROCZNA } from '../prognozaProdukcji'
import { ROK_WYMIANY_FALOWNIKA } from '../oplacalnosc'

const ulamek = (v, miejsc = 2) => v.toFixed(miejsc).replace('.', ',')
const proc = (v) => `${String(Math.round(v * 1000) / 10).replace('.', ',')}%`
const zl = (v, krok = 10) => `${(Math.round(v / krok) * krok).toLocaleString('pl-PL')} zł`

const ZRODLA_KOSZTU = {
  cena_klienta: 'cena z karty klienta',
  cena_oferty: 'cena z oferty',
  wpisana: 'kwota wpisana',
  zestaw: 'suma zestawu z montażem',
  widelki: 'podane widełki',
  typowe_widelki: 'typowy koszt dla tej mocy, bo kwoty nie podano',
}

/**
 * @param {object} wynik wynik `pieniadzeKlienta`
 * @returns {string[]} zdania do listy „Skąd te liczby”
 */
export const zalozeniaPieniedzy = (wynik) => {
  const r = wynik.rozliczenie
  const t = taryfaPoKluczu(r.taryfa)
  const z = []

  const ceny = r.strefy.map((s) => `${ulamek(s.cenaZlKwh)} zł/kWh${r.strefy.length > 1 ? ` w godzinach ${s.key === 'tania' ? 'tańszych' : 'droższych'}` : ''}`)
  const zrodloCen = r.zrodlaCen.zakup === 'klient' ? 'ceny z Twojego rachunku'
    : r.zrodlaCen.zakup === 'czesciowo' ? `cena z Twojego rachunku, a brakująca to ${ZRODLO_CEN_TARYF}`
      : ZRODLO_CEN_TARYF
  z.push(`${r.wybrana === 'nie_wiem' ? 'Taryfy nie podano, więc liczymy jak G11' : `Taryfa ${t.label}`}: ${ceny.join(', ')}, brutto z dystrybucją (${zrodloCen}). Opłat stałych (abonament, opłata mocowa) nie liczymy, bo panele ich nie zmniejszają.`)

  if (r.strefy.length > 1) {
    const sredniUdzial = r.udzialPvTania.reduce((a, b) => a + b, 0) / 12
    z.push(`Prąd z paneli zużyty u siebie liczymy w ok. ${Math.round(sredniUdzial * 100)}% po cenie tańszych godzin (${t.godzinyKrotko}), resztę po droższej. ${t.uzasadnienieUdzialu}`)
  }

  z.push(`Nadwyżka idzie do depozytu po ${ulamek(r.wartoscOddanejOdDo.od)}–${ulamek(r.wartoscOddanejOdDo.do)} zł/kWh: rynkowa cena energii (${r.system === 'rcem' ? 'RCEm' : 'RCE'}) z 2025 r. i z ostatnich 12 miesięcy, ważona godzinami, w których dom oddaje prąd, razy ${ulamek(WSPOLCZYNNIK_DEPOZYTU)}.`)

  const w1 = wynik.baza.wartoscPierwszegoRoku
  const przepada = w1 && w1.przepadaZl > 1 ? ` W pierwszym roku przepada ok. ${zl(w1.przepadaZl)}: instalacja oddaje więcej, niż dom kupuje z sieci.` : ''
  z.push(`Depozyt płaci tylko za energię z sieci (ok. ${ulamek(r.energiaZakupuZlKwh)} zł z każdej kWh), nie za dystrybucję. Niewykorzystany wraca najwyżej w ${proc(r.procentZwrotu)} wartości oddanej energii.${przepada}`)

  z.push(`Pierwszy rok w zakresie roku słabszego i lepszego (produkcja ±${proc(ZMIENNOSC_PRODUKCJI)} według PVGIS). Moc modułów spada o ${proc(DEGRADACJA_ROCZNA)} rocznie, falownik wymieniamy w ${ROK_WYMIANY_FALOWNIKA}. roku.`)

  z.push(`Ceny prądu rosną o ${proc(wynik.scenariusze.wzrostCen)} rocznie, a w wariancie ostrożnym wcale; wartość oddanej energii rośnie o połowę tego tempa.`)

  const k = wynik.baza.koszt
  if (k) {
    const kwota = k.odZl === k.doZl ? zl(k.odZl, 100)
      : `${(Math.round(k.odZl / 100) * 100).toLocaleString('pl-PL')}–${zl(k.doZl, 100)}`
    z.push(`Koszt instalacji: ${kwota} (${ZRODLA_KOSZTU[wynik.baza.zrodlaDanych.koszt] || 'podany'}). Zwrot krótszy przy niższym koszcie i korzystnych cenach, dłuższy przy wyższym koszcie i cenach bez wzrostu.`)
  }
  if (wynik.dotacja?.zrodlo === 'pme2') {
    z.push(`Dotacja PME2: 30% typowego kosztu magazynu, najwyżej ${zl(wynik.dotacja.kwotaMaksZl, 100)}. Wymaga m.in. EMS i pracy przy braku prądu, sprawdź warunki przed zakupem.`)
  }

  z.push(`Stan cen na ${STAN_TARYF}. To rachunek przy dzisiejszych przepisach i cenach, a nie obietnica.`)
  return z
}

/**
 * Ceny jednym zdaniem, do krótkich opisów założeń w ofercie i karcie klienta (bez ekranu teczki):
 * taryfa i cena kWh z sieci, wartość kWh oddanego i zasada depozytu.
 */
export const opisCen = (r) => {
  if (!r) return ''
  const t = taryfaPoKluczu(r.taryfa)
  const ceny = r.strefy.length > 1
    ? `${ulamek(r.strefy[0].cenaZlKwh)} zł/kWh w godzinach droższych i ${ulamek(r.strefy[1].cenaZlKwh)} zł/kWh w tańszych`
    : `${ulamek(r.strefy[0].cenaZlKwh)} zł/kWh`
  const skad = r.zrodlaCen.zakup === 'domyslne' ? ', średnia czterech dużych sprzedawców na 2026 r.' : ', ceny klienta'
  return `${r.wybrana === 'nie_wiem' ? 'taryfa nieznana, liczona jak G11' : `taryfa ${t.label}`}: prąd z sieci ${ceny} brutto z dystrybucją${skad}; `
    + `nadwyżka ${ulamek(r.wartoscOddanejOdDo.od)}–${ulamek(r.wartoscOddanejOdDo.do)} zł/kWh w depozycie (z 1,23), który płaci tylko za energię`
}
